import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import StatusBadge from "@/components/StatusBadge";
import ITPMappingStudio from "@/components/ITPMappingStudio";
import { toast } from "sonner";
import {
  Save, ShieldCheck, AlertTriangle, Plus, Trash2, ArrowUp, ArrowDown, RefreshCw, FileText, Loader2, FileDown,
} from "lucide-react";

const POINT_TYPES = ["NONE", "HP", "AP"];
const CANONICAL_FIELDS = [
  ["hp_ap", "HP/AP column"],
  ["order", "Order"],
  ["activity", "Activity / Description"],
  ["standard", "Applicable Standard"],
  ["criteria", "Acceptance Criteria"],
  ["frequency", "Frequency"],
  ["evidence", "Verification Records / Evidence"],
  ["responsibility", "Action Responsibility"],
  ["contractor_sign", "Contractor Sign / Date"],
  ["client_sign", "Client Sign / Date"],
];

export default function ReviewImportedITP() {
  const { id } = useParams();
  const nav = useNavigate();
  const [itp, setItp] = useState(null);
  const [saving, setSaving] = useState(false);
  const [reviewedFlag, setReviewedFlag] = useState(false);
  const [mapping, setMapping] = useState({});
  const [reExtracting, setReExtracting] = useState(false);
  const [columnCount, setColumnCount] = useState(8);

  const reloadTemplate = async () => {
    const r = await api.get(`/itp-templates/${id}`);
    setItp(prev => ({
      ...prev,
      sections: r.data.sections || [],
      hold_points: r.data.hold_points || [],
      approval_points: r.data.approval_points || [],
    }));
  };

  useEffect(() => {
    api.get(`/itp-templates/${id}`).then(r => {
      const t = r.data;
      setItp({
        ...t,
        sections: (t.sections || []).map(s => ({ ...s })),
        hold_points: t.hold_points || [],
        approval_points: t.approval_points || [],
        supporting_documents: t.supporting_documents || [],
      });
      setMapping(t.column_mapping || {});
      const maxIdx = Math.max(
        7,
        ...Object.values(t.column_mapping || {}).filter((v) => typeof v === "number"),
      );
      setColumnCount(maxIdx + 2);
    }).catch(e => toast.error(e.response?.data?.detail || "Failed to load ITP"));
  }, [id]);

  const isImported = itp?.output_format_type === "imported_docx_template";
  const confidence = itp?.extraction_confidence || "low";
  const warnings = itp?.extraction_warnings || [];
  const isLow = confidence === "low";

  // Compute the flat row grid from sections
  const rows = useMemo(() => {
    if (!itp) return [];
    const out = [];
    (itp.sections || []).forEach((s, si) => {
      (s.items || []).forEach((it, ii) => {
        out.push({ ...it, _section: s.section_name, _sectionIndex: si, _itemIndex: ii });
      });
    });
    return out;
  }, [itp]);

  const sectionNames = useMemo(() => (itp?.sections || []).map(s => s.section_name), [itp]);

  const updateRow = (si, ii, patch) => {
    setItp(prev => {
      const sections = prev.sections.map(s => ({ ...s, items: [...s.items] }));
      sections[si].items[ii] = { ...sections[si].items[ii], ...patch };
      return { ...prev, sections };
    });
  };

  const deleteRow = (si, ii) => {
    setItp(prev => {
      const sections = prev.sections.map(s => ({ ...s, items: [...s.items] }));
      sections[si].items.splice(ii, 1);
      return { ...prev, sections: sections.filter(s => s.items.length > 0 || sectionNames.length === 1) };
    });
  };

  const moveRow = (si, ii, dir) => {
    setItp(prev => {
      const sections = prev.sections.map(s => ({ ...s, items: [...s.items] }));
      const items = sections[si].items;
      const j = ii + dir;
      if (j < 0 || j >= items.length) return prev;
      [items[ii], items[j]] = [items[j], items[ii]];
      return { ...prev, sections };
    });
  };

  const moveRowToSection = (fromSi, fromIi, toSectionName) => {
    setItp(prev => {
      const sections = prev.sections.map(s => ({ ...s, items: [...s.items] }));
      const [moved] = sections[fromSi].items.splice(fromIi, 1);
      const dst = sections.find(s => s.section_name === toSectionName);
      if (dst) dst.items.push(moved);
      else sections.push({ section_name: toSectionName, items: [moved] });
      return { ...prev, sections };
    });
  };

  const addRow = (si) => {
    setItp(prev => {
      const sections = prev.sections.map(s => ({ ...s, items: [...s.items] }));
      const targetIdx = si == null ? Math.max(0, sections.length - 1) : si;
      if (sections.length === 0) sections.push({ section_name: "ITP Items", items: [] });
      sections[targetIdx].items.push({
        item_description: "",
        requirement: "",
        evidence_required: "",
        mandatory: true,
        linked_hold_point: null,
        order: "",
        standard: "",
        frequency: "",
        responsibility: "",
        contractor_sign_required: false,
        client_sign_required: false,
        notes: "",
      });
      return { ...prev, sections };
    });
  };

  const addSection = () => {
    setItp(prev => ({
      ...prev,
      sections: [...(prev.sections || []), { section_name: `Section ${(prev.sections || []).length + 1}`, items: [] }],
    }));
  };

  const renameSection = (si, name) => {
    setItp(prev => {
      const sections = prev.sections.map((s, idx) => idx === si ? { ...s, section_name: name } : s);
      return { ...prev, sections };
    });
  };

  const togglePointType = (si, ii, newType) => {
    // newType is "HP" | "AP" | "NONE"
    updateRow(si, ii, { _newPointType: newType });
  };

  // Build hold_points / approval_points from row mutations before save
  const buildPoints = () => {
    const hold_points = (itp.hold_points || []).map(hp => ({ ...hp }));
    const approval_points = (itp.approval_points || []).map(ap => ({ ...ap }));
    const sections = itp.sections.map(s => ({ ...s, items: s.items.map(i => ({ ...i })) }));

    let hpCount = hold_points.length;
    let apCount = approval_points.length;

    sections.forEach(s => s.items.forEach(it => {
      const wanted = it._newPointType;
      if (wanted === undefined) return;
      // Remove from current lists if previously linked
      const prevLinked = it.linked_hold_point;
      if (prevLinked) {
        const hpIdx = hold_points.findIndex(p => p.hp_number === prevLinked);
        if (hpIdx >= 0) hold_points.splice(hpIdx, 1);
        const apIdx = approval_points.findIndex(p => p.hp_number === prevLinked);
        if (apIdx >= 0) approval_points.splice(apIdx, 1);
      }
      if (wanted === "HP") {
        hpCount += 1;
        const num = `HP${hpCount}`;
        hold_points.push({
          hp_number: num,
          title: (it.item_description || "").slice(0, 120),
          description: it.requirement || "",
          required_evidence: it.evidence_required ? it.evidence_required.split(",").map(s => s.trim()).filter(Boolean) : [],
          point_type: "HP",
        });
        it.linked_hold_point = num;
      } else if (wanted === "AP") {
        apCount += 1;
        const num = `AP${apCount}`;
        approval_points.push({
          hp_number: num,
          title: (it.item_description || "").slice(0, 120),
          description: it.requirement || "",
          required_evidence: it.evidence_required ? it.evidence_required.split(",").map(s => s.trim()).filter(Boolean) : [],
          point_type: "AP",
        });
        it.linked_hold_point = num;
      } else {
        it.linked_hold_point = null;
      }
      delete it._newPointType;
    }));

    return { sections, hold_points, approval_points };
  };

  const saveDraft = async () => {
    if (!itp) return;
    setSaving(true);
    try {
      const { sections, hold_points, approval_points } = buildPoints();
      const payload = {
        itp_number: itp.itp_number,
        name: itp.name,
        activity: itp.activity,
        specification_reference: itp.specification_reference || "",
        revision_number: itp.revision_number || "1",
        sections,
        hold_points,
        approval_points,
        supporting_documents: itp.supporting_documents,
        column_mapping: mapping,
      };
      const { data } = await api.put(`/itp-templates/${id}/review`, payload);
      setItp({ ...itp, ...data });
      toast.success("Review draft saved.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const approve = async () => {
    if (isLow && !reviewedFlag) {
      toast.error("Please confirm you have reviewed the imported ITP mapping.");
      return;
    }
    setSaving(true);
    try {
      // Save first to flush latest grid edits
      await saveDraft();
      await api.post(`/itp-templates/${id}/approve`);
      toast.success("ITP Template published.");
      nav(`/itp/${id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Activation failed");
    } finally {
      setSaving(false);
    }
  };

  const reExtract = async () => {
    if (!isImported) {
      toast.error("Only DOCX-imported ITPs can be re-extracted.");
      return;
    }
    setReExtracting(true);
    try {
      await api.post(`/itp-templates/${id}/re-extract`, { column_mapping: mapping });
      toast.success("Re-extracted with new mapping. Review rows below.");
      const r = await api.get(`/itp-templates/${id}`);
      setItp(prev => ({
        ...prev,
        sections: r.data.sections || [],
        hold_points: r.data.hold_points || [],
        approval_points: r.data.approval_points || [],
        column_mapping: r.data.column_mapping || {},
        extraction_warnings: r.data.extraction_warnings || [],
        extraction_confidence: r.data.extraction_confidence || "low",
      }));
      setMapping(r.data.column_mapping || {});
    } catch (e) {
      toast.error(e.response?.data?.detail || "Re-extract failed");
    } finally {
      setReExtracting(false);
    }
  };

  if (!itp) return <div className="p-8 text-slate-500">Loading…</div>;

  const cantApprove = itp.status === "Approved";
  const rawStatus = itp.status || "Draft";
  const displayStatus = (() => {
    if (rawStatus === "Approved") return "Published";
    if (rawStatus === "Draft" && isImported) return "Draft";
    return rawStatus;
  })();

  return (
    <div className="p-4 md:p-8" data-testid="review-imported-itp-page">
      <Link to="/itp" className="text-sm text-slate-500 hover:underline">← ITP Templates</Link>
      <div className="mt-3 flex flex-col items-start justify-between gap-3 md:flex-row md:items-end">
        <div>
          <div className="overline text-teal-700">Review & Map ITP</div>
          <h1 className="font-heading text-3xl font-black tracking-tighter text-slate-900 sm:text-4xl">
            {itp.itp_number} — {itp.name}
          </h1>
          <p className="mt-1 text-xs text-slate-600">
            Source:{" "}
            {isImported ? <b className="text-teal-700">Uploaded DOCX</b> : <b>Manual</b>} · Confidence:{" "}
            <b className="capitalize">{confidence}</b> · {warnings.length} warning(s)
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <StatusBadge status={displayStatus} />
          <Button variant="outline" onClick={saveDraft} disabled={saving} data-testid="save-review-draft">
            <Save className="mr-2 size-4"/>Save Review Draft
          </Button>
          <Button
            onClick={approve}
            disabled={saving || cantApprove || (isLow && !reviewedFlag)}
            className="bg-teal-700 hover:bg-teal-800"
            data-testid="approve-imported-itp"
          >
            <ShieldCheck className="mr-2 size-4"/>Confirm Mapping & Publish
          </Button>
        </div>
      </div>

      <DocumentPreviewCard itp={itp} onTemplateChanged={reloadTemplate} />

      {isLow && (
        <Card className="mt-4 border-amber-300 bg-amber-50 p-4" data-testid="low-confidence-banner">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-start">
            <AlertTriangle className="size-5 shrink-0 text-amber-700"/>
            <div className="flex-1">
              <div className="font-heading text-base font-bold text-amber-900">This import needs review before activation.</div>
              <p className="mt-1 text-sm text-amber-900">
                FlowQA was unable to confidently detect all inspection rows. Use the mapping panel
                and the row grid to correct entries before publishing.
              </p>
              <label className="mt-3 flex cursor-pointer items-center gap-2 text-sm font-semibold text-amber-900">
                <Checkbox
                  checked={reviewedFlag}
                  onCheckedChange={setReviewedFlag}
                  data-testid="reviewed-flag"
                />
                I have reviewed the imported ITP mapping.
              </label>
            </div>
          </div>
        </Card>
      )}

      {warnings.length > 0 && (
        <Card className="mt-4 border-slate-200 p-3" data-testid="extraction-warnings">
          <div className="overline mb-1">Extraction warnings</div>
          <ul className="ml-4 list-disc text-xs text-slate-700">
            {warnings.map((w, i) => <li key={i}>{w}</li>)}
          </ul>
        </Card>
      )}

      {/* Header editable */}
      <Card className="mt-4 border-slate-200 p-4" data-testid="review-header">
        <div className="overline mb-2">Header</div>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          {[
            ["itp_number","ITP Number"],
            ["name","Template Name"],
            ["activity","Activity / Category"],
            ["specification_reference","Specification Reference"],
            ["revision_number","Revision"],
          ].map(([k,l]) => (
            <div key={k}>
              <Label className="text-xs">{l}</Label>
              <Input
                value={itp[k] || ""}
                onChange={(e) => setItp({ ...itp, [k]: e.target.value })}
                data-testid={`review-header-${k}`}
              />
            </div>
          ))}
        </div>
      </Card>

      {/* Mapping editor (only for imported DOCX) */}
      {isImported && (
        <Card className="mt-4 border-teal-200 bg-teal-50/30 p-4" data-testid="mapping-editor">
          <div className="flex items-center justify-between">
            <div>
              <div className="overline">Column Mapping (source DOCX → FlowQA fields)</div>
              <p className="text-xs text-slate-600">
                Map each FlowQA canonical field to the column index that contains that data in the source DOCX.
                Leave blank if the source DOCX has no such column. Use Re-extract to re-parse rows with this mapping.
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={reExtract}
              disabled={reExtracting}
              data-testid="re-extract-btn"
            >
              <RefreshCw className={`mr-2 size-4 ${reExtracting ? "animate-spin" : ""}`}/>
              {reExtracting ? "Re-extracting…" : "Re-extract with mapping"}
            </Button>
          </div>
          <div className="mt-3 grid grid-cols-1 gap-2 md:grid-cols-2 lg:grid-cols-3">
            {CANONICAL_FIELDS.map(([key, label]) => (
              <div key={key}>
                <Label className="text-xs">{label}</Label>
                <Select
                  value={mapping[key] != null ? String(mapping[key]) : "none"}
                  onValueChange={(v) => setMapping(m => ({ ...m, [key]: v === "none" ? null : Number(v) }))}
                >
                  <SelectTrigger data-testid={`mapping-${key}`}><SelectValue placeholder="Not in source"/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Not in source</SelectItem>
                    {Array.from({ length: columnCount }, (_, i) => (
                      <SelectItem key={i} value={String(i)}>Column {i + 1}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Section toolbar */}
      <div className="mt-6 flex items-center justify-between">
        <div className="overline">Row Grid · {rows.length} item(s)</div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={addSection} data-testid="add-section-btn">
            <Plus className="mr-1.5 size-3.5"/>Add Section
          </Button>
        </div>
      </div>

      <Card className="mt-2 overflow-hidden border-slate-200" data-testid="row-grid">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1400px] text-xs">
            <thead className="bg-slate-50 text-left uppercase tracking-wider text-slate-600">
              <tr>
                <th className="px-2 py-2">Section</th>
                <th className="px-2 py-2">HP/AP</th>
                <th className="px-2 py-2">Order</th>
                <th className="px-2 py-2">Activity / Description</th>
                <th className="px-2 py-2">Applicable Standard</th>
                <th className="px-2 py-2">Acceptance Criteria</th>
                <th className="px-2 py-2">Frequency</th>
                <th className="px-2 py-2">Verification Records</th>
                <th className="px-2 py-2">Responsibility</th>
                <th className="px-2 py-2">Contr. Sign</th>
                <th className="px-2 py-2">Client Sign</th>
                <th className="px-2 py-2">Evidence Req&apos;d</th>
                <th className="px-2 py-2">Notes</th>
                <th className="px-2 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {(itp.sections || []).map((s, si) => (
                <ReviewSectionRows
                  key={si}
                  s={s}
                  si={si}
                  sectionNames={sectionNames}
                  updateRow={updateRow}
                  deleteRow={deleteRow}
                  moveRow={moveRow}
                  moveRowToSection={moveRowToSection}
                  togglePointType={togglePointType}
                  addRow={addRow}
                  renameSection={renameSection}
                />
              ))}
              {(!itp.sections || itp.sections.length === 0) && (
                <tr><td colSpan={14} className="px-4 py-8 text-center text-slate-500">
                  No rows yet. <button className="font-semibold text-teal-700" onClick={() => addRow(null)}>Add a row</button>
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Supporting Documents */}
      <Card className="mt-4 border-slate-200 p-4" data-testid="supporting-docs">
        <div className="flex items-center justify-between">
          <div>
            <div className="overline">Supporting Documents</div>
            <p className="text-xs text-slate-600">Add documents required by this ITP (one per line).</p>
          </div>
          <FileText className="size-5 text-slate-400"/>
        </div>
        <Textarea
          className="mt-2"
          rows={3}
          value={(itp.supporting_documents || []).join("\n")}
          onChange={(e) => setItp({ ...itp, supporting_documents: e.target.value.split("\n").map(s => s.trim()).filter(Boolean) })}
          placeholder="e.g. Reinforcement schedule&#10;Concrete mix design"
          data-testid="supporting-docs-input"
        />
      </Card>

      {/* HP & AP summary chips */}
      <Card className="mt-4 border-slate-200 p-4">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <div className="overline text-teal-700">Hold Points ({(itp.hold_points || []).length})</div>
            <div className="mt-2 flex flex-wrap gap-1">
              {(itp.hold_points || []).map(hp => (
                <span key={hp.hp_number} className="rounded bg-teal-100 px-2 py-0.5 text-[11px] font-semibold text-teal-800" data-testid={`hp-chip-${hp.hp_number}`}>
                  {hp.hp_number} · {hp.title}
                </span>
              ))}
              {(itp.hold_points || []).length === 0 && <span className="text-xs text-slate-400">No hold points.</span>}
            </div>
          </div>
          <div>
            <div className="overline text-indigo-700">Action Points ({(itp.approval_points || []).length})</div>
            <div className="mt-2 flex flex-wrap gap-1">
              {(itp.approval_points || []).map(ap => (
                <span key={ap.hp_number} className="rounded bg-indigo-100 px-2 py-0.5 text-[11px] font-semibold text-indigo-800" data-testid={`ap-chip-${ap.hp_number}`}>
                  {ap.hp_number} · {ap.title}
                </span>
              ))}
              {(itp.approval_points || []).length === 0 && <span className="text-xs text-slate-400">No action points.</span>}
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}

function ReviewSectionRows({
  s, si, sectionNames, updateRow, deleteRow, moveRow, moveRowToSection, togglePointType, addRow, renameSection,
}) {
  return (
    <>
      <tr className="bg-slate-100/70" data-testid={`section-header-${si}`}>
        <td colSpan={14} className="px-2 py-1.5">
          <div className="flex items-center justify-between">
            <input
              value={s.section_name}
              onChange={(e) => renameSection(si, e.target.value)}
              className="border-none bg-transparent text-xs font-bold uppercase tracking-wider text-slate-700 focus:outline-none"
              data-testid={`section-name-${si}`}
            />
            <Button size="sm" variant="ghost" onClick={() => addRow(si)} className="h-7 px-2 text-teal-700" data-testid={`add-row-${si}`}>
              <Plus className="mr-1 size-3"/>Add Row
            </Button>
          </div>
        </td>
      </tr>
      {s.items.map((it, ii) => {
        const currentType = it._newPointType ?? (it.linked_hold_point ? (String(it.linked_hold_point).toUpperCase().startsWith("AP") ? "AP" : "HP") : "NONE");
        return (
          <tr key={ii} className="border-t border-slate-100" data-testid={`review-row-${si}-${ii}`}>
            <td className="px-2 py-1">
              <Select value={s.section_name} onValueChange={(v) => moveRowToSection(si, ii, v)}>
                <SelectTrigger className="h-8" data-testid={`row-section-${si}-${ii}`}><SelectValue/></SelectTrigger>
                <SelectContent>
                  {sectionNames.map(name => <SelectItem key={name} value={name}>{name}</SelectItem>)}
                </SelectContent>
              </Select>
            </td>
            <td className="px-2 py-1">
              <Select value={currentType} onValueChange={(v) => togglePointType(si, ii, v)}>
                <SelectTrigger className="h-8 w-20" data-testid={`row-point-${si}-${ii}`}><SelectValue/></SelectTrigger>
                <SelectContent>
                  {POINT_TYPES.map(t => <SelectItem key={t} value={t}>{t === "NONE" ? "—" : t}</SelectItem>)}
                </SelectContent>
              </Select>
            </td>
            <td className="px-2 py-1">
              <Input className="h-8 w-16" value={it.order || ""} onChange={(e) => updateRow(si, ii, { order: e.target.value })} data-testid={`row-order-${si}-${ii}`}/>
            </td>
            <td className="px-2 py-1">
              <Input className="h-8 min-w-[200px]" value={it.item_description || ""} onChange={(e) => updateRow(si, ii, { item_description: e.target.value })} data-testid={`row-desc-${si}-${ii}`}/>
            </td>
            <td className="px-2 py-1">
              <Input className="h-8" value={it.standard || ""} onChange={(e) => updateRow(si, ii, { standard: e.target.value })}/>
            </td>
            <td className="px-2 py-1">
              <Input className="h-8 min-w-[160px]" value={it.requirement || ""} onChange={(e) => updateRow(si, ii, { requirement: e.target.value })}/>
            </td>
            <td className="px-2 py-1">
              <Input className="h-8" value={it.frequency || ""} onChange={(e) => updateRow(si, ii, { frequency: e.target.value })}/>
            </td>
            <td className="px-2 py-1">
              <Input className="h-8" value={it.evidence_required || ""} onChange={(e) => updateRow(si, ii, { evidence_required: e.target.value })}/>
            </td>
            <td className="px-2 py-1">
              <Input className="h-8" value={it.responsibility || ""} onChange={(e) => updateRow(si, ii, { responsibility: e.target.value })}/>
            </td>
            <td className="px-2 py-1 text-center">
              <Checkbox checked={!!it.contractor_sign_required} onCheckedChange={(v) => updateRow(si, ii, { contractor_sign_required: !!v })}/>
            </td>
            <td className="px-2 py-1 text-center">
              <Checkbox checked={!!it.client_sign_required} onCheckedChange={(v) => updateRow(si, ii, { client_sign_required: !!v })}/>
            </td>
            <td className="px-2 py-1">
              <Input className="h-8" value={(it.required_evidence || []).join(", ")} onChange={(e) => updateRow(si, ii, { required_evidence: e.target.value.split(",").map(x => x.trim()).filter(Boolean) })}/>
            </td>
            <td className="px-2 py-1">
              <Input className="h-8 min-w-[120px]" value={it.notes || ""} onChange={(e) => updateRow(si, ii, { notes: e.target.value })}/>
            </td>
            <td className="px-2 py-1">
              <div className="flex items-center gap-1">
                <Button variant="ghost" size="icon" className="size-7" onClick={() => moveRow(si, ii, -1)} title="Move up" data-testid={`row-up-${si}-${ii}`}><ArrowUp className="size-3.5"/></Button>
                <Button variant="ghost" size="icon" className="size-7" onClick={() => moveRow(si, ii, 1)} title="Move down" data-testid={`row-down-${si}-${ii}`}><ArrowDown className="size-3.5"/></Button>
                <Button variant="ghost" size="icon" className="size-7 text-red-600 hover:bg-red-50" onClick={() => deleteRow(si, ii)} title="Delete" data-testid={`row-delete-${si}-${ii}`}><Trash2 className="size-3.5"/></Button>
              </div>
            </td>
          </tr>
        );
      })}
    </>
  );
}

function DocumentPreviewCard({ itp, onTemplateChanged }) {
  const isUploadedDocx = itp.source_type === "uploaded_docx" || itp.output_format_type === "imported_docx_template";
  const [proc, setProc] = useState(null);
  const [retrying, setRetrying] = useState(false);

  useEffect(() => {
    if (!isUploadedDocx) return undefined;
    let stop = false;
    const poll = async () => {
      try {
        const r = await api.get(`/itp-templates/${itp.id}/processing-status`);
        if (stop) return;
        setProc(r.data);
        if (r.data.render_status === "processing") setTimeout(poll, 2500);
      } catch { /* transient */ }
    };
    poll();
    return () => { stop = true; };
  }, [itp.id, isUploadedDocx, retrying]);

  const renderStatus = proc?.render_status || "processing";

  const downloadFile = async (path, fname, mime) => {
    try {
      const r = await api.get(path, { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(new Blob([r.data], { type: mime }));
      a.download = fname;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(a.href), 1500);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Download failed");
    }
  };

  const retry = async () => {
    setRetrying(true);
    try {
      await api.post(`/itp-templates/${itp.id}/retry-conversion`);
      toast.success("Conversion restarted.");
      setProc(p => ({ ...(p || {}), render_status: "processing", render_error: null }));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Retry failed");
    } finally {
      setRetrying(false);
    }
  };

  if (!isUploadedDocx) return null;
  const detection = proc?.automatic_detection_status || itp.automatic_detection_status || "—";
  const safeName = (itp.itp_number || "ITP").replace(/[^\w.-]+/g, "_");

  return (
    <Card className="mt-4 border-slate-200 p-4" data-testid="itp-document-preview-card">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="overline text-teal-700">Source Document</div>
          <div className="text-sm font-semibold text-slate-900" data-testid="itp-source-filename">
            {proc?.source_file_name || itp.source_file_name || "Uploaded DOCX"}
          </div>
          <div className="mt-0.5 text-xs text-slate-500">
            Automatic detection: <b data-testid="itp-detection-status">{detection}</b>
            {proc?.page_count ? <> · {proc.page_count} page{proc.page_count === 1 ? "" : "s"}</> : null}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => downloadFile(`/itp-templates/${itp.id}/source.docx`, `${safeName}_source.docx`, "application/vnd.openxmlformats-officedocument.wordprocessingml.document")} data-testid="download-original-docx-btn">
            <FileDown className="mr-1.5 size-4"/> Original DOCX
          </Button>
          {renderStatus === "completed" && (
            <Button variant="outline" size="sm" onClick={() => downloadFile(`/itp-templates/${itp.id}/rendered.pdf`, `${safeName}_preview.pdf`, "application/pdf")} data-testid="download-rendered-pdf-btn">
              <FileDown className="mr-1.5 size-4"/> Rendered PDF
            </Button>
          )}
          {renderStatus === "failed" && (
            <Button variant="outline" size="sm" onClick={retry} disabled={retrying} className="border-rose-300 text-rose-700 hover:bg-rose-50" data-testid="retry-conversion-btn">
              <RefreshCw className={`mr-1.5 size-4 ${retrying ? "animate-spin" : ""}`}/> Retry Conversion
            </Button>
          )}
        </div>
      </div>

      {renderStatus === "processing" && (
        <div className="mt-3 flex items-center gap-2 rounded-md border border-slate-200 bg-slate-50 p-3 text-sm text-slate-600" data-testid="render-processing-state">
          <Loader2 className="size-4 animate-spin text-teal-700"/> Converting document and creating page previews…
        </div>
      )}
      {renderStatus === "failed" && (
        <div className="mt-3 rounded-md border border-rose-200 bg-rose-50 p-3 text-sm text-rose-900" data-testid="render-failed-state">
          {proc?.render_error || "FlowQA saved the original ITP, but could not generate the document preview. The file has not been lost. Retry conversion or download the original document."}
        </div>
      )}
      {renderStatus === "completed" && (
        <ITPMappingStudio itp={itp} onTemplateChanged={onTemplateChanged} />
      )}
    </Card>
  );
}
